import { useState, useCallback } from "react";
import apiSubscription from "../../services/subscription";

/**
 * Manual billing identity duplicate check.
 *
 * Hits POST /subscriptionV2/check-billing-identity which checks the
 * CustomerInvoice tax_number + name_branch combo (or id_passport for
 * personal). Triggered on demand by the caller — typically the
 * CompanyStep submit handler.
 *
 * Why manual instead of auto-debounce:
 *   The earlier debounced version watched form values and fired on
 *   every change — that's noisy for a check whose only purpose is to
 *   gate the "Next" button. Doing it once per submit keeps the
 *   network footprint minimal and the UX honest: the user sees the
 *   error in response to their action, not while they're still typing.
 *
 * Returned shape:
 *   loading: boolean        — true while a check is in flight
 *   error: { code, message, field } | null   — duplicate found
 *   networkError: string | null              — transport failure
 *   check(input): Promise<{ok, error?}>      — call to run the check
 *   reset(): void                            — clear state (e.g. when
 *                                              user edits after error)
 *
 * The `check()` resolution mirrors the backend response so callers
 * can decide what to do without reading state:
 *   { ok: true }
 *   { ok: false, error: { code, message, field } }
 *   { ok: false, networkError: "..." }
 */
function useCheckBillingIdentity() {
  const [state, setState] = useState({
    loading: false,
    error: null,
    networkError: null,
  });

  const reset = useCallback(() => {
    setState({ loading: false, error: null, networkError: null });
  }, []);

  const check = useCallback(async ({
    id_project,
    detail_type,
    tax_number,
    name_branch,
  }) => {
    setState({ loading: true, error: null, networkError: null });

    try {
      const res = await apiSubscription.checkBillingIdentity({
        id_project,
        detail_type,
        tax_number: (tax_number || "").trim(),
        name_branch: name_branch || undefined,
      });

      if (res && res.ok === false) {
        const error = {
          code: res.code,
          message: res.message,
          field: res.field,
        };
        setState({ loading: false, error, networkError: null });
        return { ok: false, error };
      }

      setState({ loading: false, error: null, networkError: null });
      return { ok: true };
    } catch (err) {
      const networkError = err?.message || "Billing identity check failed";
      setState({ loading: false, error: null, networkError });
      return { ok: false, networkError };
    }
  }, []);

  return { ...state, check, reset };
}

export default useCheckBillingIdentity;